import { useState } from "react";
import { withFormik, Form, Field } from "formik";
import * as Yup from "yup";
import { FormGroup, Label, Button } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMutation } from "@tanstack/react-query";
import { CheckIfEmailIsOccupied } from "../../actions/teacherMutations";
import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "react-toastify";
import Select from "react-select";
import AddNewTeacherToClass from "../Modal/AddNewTeacherToClass";
import DataGrid from "../DataGrids/DataGrid";
import { v4 as uuid } from "uuid";
import { compareByName } from "../../Utilities/sortHelper";

const CreateClassInProgramForm = ({
  values,
  errors,
  touched,
  isSubmitting,
  toggleModal,
  newTeachersToAdd,
  setNewTeachersToAdd,
}) => {
  const intl = useIntl();
  const [teachersToAdd, setTeachersToAdd] = useState([]);
  const [teachersForAdding, setTeachersForAdding] = useState(values.teachers);
  const [addNewTeacherModalOpen, setAddNewTeacherModalOpen] = useState(false);
  const mutateCheckEmailTaken = useMutation({ mutationFn: CheckIfEmailIsOccupied });
  const handleChange = (selected) => {
    if (selected.value === "AddNewTeacher") {
      setAddNewTeacherModalOpen(true);
    } else {
      let teacherToAdd = {
        id: selected.id,
        name: selected.name,
        email: selected.email,
        fullAccess: selected.fullAccess,
        purchaser: selected.purchaser,
        isNewTeacher: selected.isNewTeacher,
      };
      setTeachersToAdd([...teachersToAdd, teacherToAdd]);
      setTeachersForAdding(
        teachersForAdding.filter((teacher) => teacher.id !== selected.id)
      );
    }
  };

  const removeTeacherFromAddList = (teacher) => {
    if (newTeachersToAdd.some((nt) => nt.id === teacher.id)) {
      setNewTeachersToAdd(newTeachersToAdd.filter((t) => t.id !== teacher.id));
    } else {
      setTeachersToAdd([
        ...teachersToAdd.filter(function (t) {
          return t.email !== teacher.email;
        }),
      ]);
      setTeachersForAdding([...teachersForAdding, teacher]);
    }
  };

  const handleAddNewTeacher = (values) => {
    toggleAddTeacherModal();
    mutateCheckEmailTaken.mutate(
      {
        newEmail: values.email,
      },
      {
        onSuccess: (data) => {
          if (data && data.status > 199 && data.status < 300) {
            if (data.data === true) {
              toast.warn(<FormattedMessage id="EmailOccupied" />);
            } else {
              let newTeacherToAdd = {
                id: uuid(),
                email: values.email,
                fullAccess: values.fullAccess,
                name: values.name,
                purchaser: values.purchaser,
                isNewTeacher: true,
              };
              setNewTeachersToAdd([...newTeachersToAdd, newTeacherToAdd]);
            }
          }
        },
      }
    );
  };

  let teachersDataSource = [];
  teachersToAdd.forEach((teacher, i) => {
    let teacherToAdd = {
      uniqueId: teacher.id,
      name: teacher.name,
      email: teacher.email,
      fullAccess: teacher.fullAccess ? (
        <FormattedMessage id="true" />
      ) : (
        <FormattedMessage id="false" />
      ),
      purchaser: teacher.purchaser ? (
        <FormattedMessage id="true" />
      ) : (
        <FormattedMessage id="false" />
      ),
      edit: (
        <Button
          color="danger"
          outline
          size="sm"
          onClick={() => removeTeacherFromAddList(teacher)}
        >
          <FontAwesomeIcon icon="trash" />
        </Button>
      ),
    };
    teachersDataSource.push(teacherToAdd);
  });

  newTeachersToAdd.forEach((newTeacher) => {
    let newTeacherToAdd = {
      uniqueId: newTeacher.id,
      name: newTeacher.name,
      email: newTeacher.email,
      fullAccess: newTeacher.fullAccess ? (
        <FormattedMessage id="true" />
      ) : (
        <FormattedMessage id="false" />
      ),
      purchaser: newTeacher.purchaser ? (
        <FormattedMessage id="true" />
      ) : (
        <FormattedMessage id="false" />
      ),
      edit: (
        <Button
          color="danger"
          outline
          size="sm"
          onClick={() => removeTeacherFromAddList(newTeacher)}
        >
          <FontAwesomeIcon icon="trash" />
        </Button>
      ),
    };
    teachersDataSource.push(newTeacherToAdd);
  });

  const toggleAddTeacherModal = () => {
    setAddNewTeacherModalOpen((prev) => !prev);
  };

  const teachersTableColumns = [
    { id: "edit", key: "edit", header: " ", width: 60, sortable: false },
    {
      id: "name",
      key: "name",
      header: () => <FormattedMessage id="Name" />,
      width: 180,
      sortable: false,
    },
    {
      id: "email",
      key: "email",
      header: () => <FormattedMessage id="Email" />,
      width: 200,
      sortable: false,
    },
    {
      id: "fullAccess",
      key: "fullAccess",
      header: () => <FormattedMessage id="FullTeacherAccess" />,
      width: 180,
      sortable: false,
    },
    {
      id: "purchaser",
      key: "purchaser",
      header: () => <FormattedMessage id="Purchaser" />,
      width: 150,
      sortable: false,
    },
  ];

  const selectableTeachers = () => {
    // Sortera före unshift så att "Lägg till ny lärare" ligger kvar överst.
    let teachers = [...teachersForAdding].sort(compareByName).map((option) => ({
      ...option,
      key: option.id,
      label: option.name,
      value: option.id,
      clearableValue: values.fullAccess ? true : false,
    }));
    teachers.unshift({
      label: intl.formatMessage({ id: "AddNewTeacher" }),
      value: "AddNewTeacher",
    });
    return teachers;
  };

  return (
    <div className="col-12 col-sm-12">
      <Form>
        <FormGroup>
          <Label>
            <FormattedMessage id="ClassName" />
          </Label>
          <Field
            className={`form-control ${
              errors.name && touched.name && "is-invalid"
            }`}
            type="input"
            name="name"
            placeholder={intl.formatMessage({ id: "ClassName" })}
          />
          {errors.name && touched.name && (
            <div className="invalid-feedback">{errors.name}</div>
          )}
        </FormGroup>

        <FormGroup>
          <Label for="selectTeachers">
            <FormattedMessage id="SelectOneOrMoreTeachers" />
          </Label>
          <Select
            placeholder={<FormattedMessage id="ChooseTeacherPlaceholder" />}
            isMulti={false}
            noOptionsMessage={() => (
              <FormattedMessage id="NoSelectableTeachersInClass" />
            )}
            onChange={(selected) => handleChange(selected)}
            options={selectableTeachers()}
            defaultValue={
              !values.fullAccess
                ? {
                    label: values.preselectedTeacher.name,
                    value: values.preselectedTeacher.id,
                    clearableValue: false,
                  }
                : ""
            }
          />
        </FormGroup>
        {teachersToAdd.length > 0 || newTeachersToAdd.length > 0 ? (
          <DataGrid columns={teachersTableColumns} rows={teachersDataSource} />
        ) : (
          ""
        )}

        <Button
          type="submit"
          outline
          color="primary"
          className="btn float-end"
          disabled={isSubmitting}
          onClick={() => (values.teachersToAdd = teachersToAdd)}
        >
          {isSubmitting ? (
            <FormattedMessage id="Wait" />
          ) : (
            <FormattedMessage id="Add" />
          )}
        </Button>
        <Button
          type="button"
          outline
          color="secondary"
          onClick={toggleModal}
          className="float-end me-2"
        >
          <FormattedMessage id="Cancel" />
        </Button>
      </Form>
      <AddNewTeacherToClass
        isOpen={addNewTeacherModalOpen}
        toggle={toggleAddTeacherModal}
        onSubmit={handleAddNewTeacher}
        fullAccessTeacher={values.fullAccess}
        purchaserTeacher={true}
      />
    </div>
  );
};

const FormikForm = withFormik({
  //todo-preselct teacher with loggedin if not fullaccess....
  mapPropsToValues: (props) => ({
    name: "",
    school: props.school,
    teachers: props.teachers,
    orientations: props.orientations ? props.orientations : [],
    selectedExistingTeacherIds: props.fullAccess
      ? []
      : props.preselectedTeacher
      ? props.preselectedTeacher.id
      : 0,
    selectedProgramme: null,
    selectedOrientation: "",
    fullAccess: props.fullAccess ? props.fullAccess : true,
  }),
  validationSchema: Yup.object().shape({
    name: Yup.string()
      .required(
        <FormattedMessage id="Views.Teacher.CreateClassForm.Error.RequireName" />
      )
      .matches(
        /^[A-Za-z0-9,\-,", Å,å,Ä,ä,Ö,ö ]+$/,
        <FormattedMessage id="InvalidClassNameError" />
      ),
  }),

  handleSubmit(values, { props }) {
    let teacherIdsToAdd = [];
    let cl;
    values.teachersToAdd.forEach((t) => {
      teacherIdsToAdd.push(t.id);
    });
    cl = {
      name: values.name,
      school: values.school,
      programme: props.program,
      orientation: { id: 0, name: values.selectedOrientation },
      teachers: teacherIdsToAdd,
    };
    props.handleAddedClass(cl);
  },
})(CreateClassInProgramForm);

export default FormikForm;
